
import Vue from 'vue';
import Component from "vue-class-component";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    AreaTypeModel,
    AreaTypesClient,
    CreateOrUpdateAreaRequest,
    factory,
    ResultError,
    SysAdminAreasClient,
} from "@/api-client";
import toasts from "@/components/Toasts";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import {Route} from "vue-router";
import {Prop, Watch} from "vue-property-decorator";
import {Culture, cultureService} from "@/CultureService";
import ErrorsSummary from "@/components/ErrorsSummary.vue";

@Component({
    components: {
        HeadingWave,
        LabelOptional,
        AdministrationDetailviewLayout,
        ErrorsSummary
    },
})
export default class UpsertArea extends Vue {
    async beforeRouteEnter(to: Route, from: Route, next: Function) {
        try {
            if (to.params.id) {
                const areaToUpdate = await new SysAdminAreasClient().details(to.params.id);
                to.meta!.props.model = new CreateOrUpdateAreaRequest({
                    nameEng: areaToUpdate.nameEng,
                    nameEst: areaToUpdate.nameEst,
                    typeKeys: areaToUpdate.typeKeys,
                    additionalInformation: areaToUpdate.additionalInformation
                });

                to.meta!.props.isActive = areaToUpdate.isActive;
            }
            to.meta!.props.areaTypeOptions = await new AreaTypesClient().all();
            next();
        } catch (e) {
            next(e);
        }
    }

    @Prop({required: false, default: () => factory.create(CreateOrUpdateAreaRequest)})
    model!: CreateOrUpdateAreaRequest;

    @Prop({required: false, default: () => true})
    isActive!: boolean;
    @Prop({required: true})
    areaTypeOptions!: AreaTypeModel[];

    validationErrors: ResultError = ResultError.createEmpty();
    selectedTypes: AreaTypeModel[] = [];

    mounted() {
        this.syncSelectedTypes();
    }

    // @Watch('model.typeKeys')
    // onTypeKeysChanged() {
    //     this.syncSelectedTypes();
    // }

    @Watch('selectedTypes')
    onSelectedTypesChanged() {
        this.model.typeKeys = this.selectedTypes.map(t => t.key);
    }

    syncSelectedTypes() {
        if (this.model.typeKeys && this.areaTypeOptions) {
            this.selectedTypes = this.areaTypeOptions.filter(option =>
                this.model.typeKeys!.includes(option.key)
            );
        }
    }

    get isEditMode(): boolean {
        return this.$route.params.id != null;
    }

    get isActiveLabel(): string {
        return `${this.$ls.Administration_Areas_CurrentActivity} ${this.isActive ? this.$ls.Global_Label_Active : this.$ls.Global_Label_InActive}`;
    }

    get activationButtonText(): string {
        return this.isActive ? this.$ls.Administration_Areas_Deactivate_Action : this.$ls.Administration_Areas_Activate_Action;
    }

    get id(): string | null {
        return this.$route.params.id;
    }

    async save() {
        this.validationErrors = ResultError.createEmpty();
        if (this.isEditMode) {
            await new SysAdminAreasClient().update(this.$route.params.id, this.model)
                .then(
                    successResponse => {
                        this.$router.push({name: 'areas'});
                        toasts.success(`${this.$ls.Administration_Areas_Toasts_Update_Success}: ${this.getNameInCurrentCulture()}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminAreasClient().create(this.model)
                .then(
                    successResponse => {
                        this.$router.push({name: 'areas'});
                        toasts.success(`${this.$ls.Administration_Areas_Toasts_Adding_Success}: ${this.getNameInCurrentCulture()}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }

    async toggleActivity() {
        this.validationErrors = ResultError.createEmpty();

        if (this.isActive) {
            await new SysAdminAreasClient().deactivate(this.$route.params.id)
                .then(
                    successResponse => {
                        this.$router.push({name: 'areas'});
                        toasts.success(`${this.$ls.Administration_Areas_Toasts_Deactivation_Success}: ${this.getNameInCurrentCulture()}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        } else {
            await new SysAdminAreasClient().activate(this.$route.params.id)
                .then(
                    successResponse => {
                        this.$router.push({name: 'areas'});
                        toasts.success(`${this.$ls.Administration_Areas_Toasts_Activation_Success}: ${this.getNameInCurrentCulture()}`);
                    },
                    (errorResponse: ResultError) => {
                        this.validationErrors = errorResponse;
                        toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                    });
        }
    }

    getNameInCurrentCulture() {
        return cultureService.currentCulture === Culture.Estonian ? this.model.nameEst : this.model.nameEng;
    }
}
